// Land → Spaceframe Chassis feature-tree windows (the `land/spaceframe`
// surface). Tree order is the user's: FIA Spec → … → Structural (FEA), then the
// two PDF items at the bottom (FIA Regulations, Compliance Report).
//
// Every window is React.lazy: the registry is imported by the always-loaded
// sidebar, and the spaceframe windows pull the workbench store, catalog data
// and viewer helpers — none of which may land in the first-paint bundle
// (Lazy3D boundary; `npm run lint:bundle` / `lint:entry-budget`).

import { createElement, lazy, Suspense, type ComponentType } from 'react';
import type { FeatureWindowItem } from './featureWindowRegistry';

type WindowProps = { enabled: boolean };

type Mod = Record<string, ComponentType<WindowProps>>;
// One dynamic import per window file (each window is its own lazy chunk).
const LOADERS: Record<string, () => Promise<unknown>> = {
  FiaSpecWindow: () => import('@/components/spaceframe/windows/FiaSpecWindow'),
  OccupantWindow: () =>
    import('@/components/spaceframe/windows/OccupantWindow'),
  SketchWindow: () => import('@/components/spaceframe/windows/SketchWindow'),
  TubeSpecWindow: () =>
    import('@/components/spaceframe/windows/TubeSpecWindow'),
  WeldmentWindow: () =>
    import('@/components/spaceframe/windows/WeldmentWindow'),
  BendsWindow: () => import('@/components/spaceframe/windows/BendsWindow'),
  ComplianceWindow: () =>
    import('@/components/spaceframe/windows/ComplianceWindow'),
  FeaWindow: () => import('@/components/spaceframe/windows/FeaWindow'),
  FiaRegulationsWindow: () =>
    import('@/components/spaceframe/windows/FiaRegulationsWindow'),
  ComplianceReportWindow: () =>
    import('@/components/spaceframe/windows/ComplianceReportWindow'),
};

const load = (name: string): ComponentType<WindowProps> =>
  lazy(() => LOADERS[name]().then((m) => ({ default: (m as Mod)[name] })));

const ROWS: { key: string; label: string; width: number; name: string }[] = [
  { key: 'sf-fia-spec', label: 'FIA Spec', width: 380, name: 'FiaSpecWindow' },
  {
    key: 'sf-occupant',
    label: 'Vehicle & Occupant',
    width: 380,
    name: 'OccupantWindow',
  },
  { key: 'sf-sketch', label: '3D Sketch', width: 380, name: 'SketchWindow' },
  {
    key: 'sf-tube-spec',
    label: 'Tube Spec',
    width: 400,
    name: 'TubeSpecWindow',
  },
  { key: 'sf-weldment', label: 'Weldment', width: 360, name: 'WeldmentWindow' },
  {
    key: 'sf-bends',
    label: 'Bends & Fabrication',
    width: 420,
    name: 'BendsWindow',
  },
  {
    key: 'sf-compliance',
    label: 'Compliance',
    width: 380,
    name: 'ComplianceWindow',
  },
  { key: 'sf-fea', label: 'Structural (FEA)', width: 380, name: 'FeaWindow' },
  {
    key: 'sf-fia-docs',
    label: 'FIA Regulations (PDF)',
    width: 460,
    name: 'FiaRegulationsWindow',
  },
  {
    key: 'sf-report',
    label: 'Compliance Report (PDF)',
    width: 520,
    name: 'ComplianceReportWindow',
  },
];

/** Flag-off legacy pane (feature-tree windows kill-switch OFF): the same 10
 *  windows stacked inline in the sidebar, in tree order. */
export function SpaceframeInlinePane({ enabled }: { enabled: boolean }) {
  return createElement(
    'div',
    { className: 'space-y-4' },
    SPACEFRAME_FEATURE_WINDOWS.map((w) =>
      createElement(
        'section',
        { key: w.key },
        createElement(
          'h3',
          {
            className: 'mb-2 text-xs font-semibold text-telekinesis-neutral-50',
          },
          w.label,
        ),
        w.render(enabled),
      ),
    ),
  );
}

export const SPACEFRAME_FEATURE_WINDOWS: FeatureWindowItem[] = ROWS.map(
  ({ key, label, width, name }) => {
    const Comp = load(name);
    return {
      key,
      label,
      width,
      render: (enabled: boolean) =>
        createElement(
          Suspense,
          { fallback: null },
          createElement(Comp, { enabled }),
        ),
    };
  },
);
